import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import Moment from 'react-moment';
import { Link, navigate } from 'raviger';
import { Button, Card, Col, Collapse, ListGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faUserMd,
  faCheckSquare,
  faTimes,
  faArrowAltCircleLeft,
  faPrescription,
  faChevronCircleUp,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFolderMedical,
  faSquareX,
  faSyringe,
  faHeart,
  faCancel,
  faSquareChevronRight,
  faSquareChevronLeft,
  faChevronCircleDown,
  faCalendarExclamation,
  faVial,
  faScalpelLineDashed,
  faPrescriptionBottlePill
} from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import Timeline, {
  TimelineHeaders,
  DateHeader
} from 'react-calendar-timeline';
import moment from 'moment';
import Patient from './components/Patient';
import Header from '../components/Header';
import LoadingLink from '../components/LoadingLink';
import ActionsDropdown from '../components/ActionsDropdown';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';

function PatientCalendarView({ id, incident, organization }) {

  const renderGroup = ({ group }) => {
    return (
      <div key={group.id} style={{whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}} >
        <Link href={group.vet_type === "treatment_plan" ? ("/" + organization + "/" + incident + "/vet/treatment/" + group.object_id) : group.vet_type === 'diagnostic' ? ("/" + organization + "/" + incident + "/vet/diagnosticresult/edit/" + group.object_id) : ("/" + organization + "/" + incident + "/vet/procedureresult/edit/" + group.object_id)} className="underline-link" title={group.title + (group.is_overdue ? " !" : "")} style={{color:"white", textDecoration: "none"}}>
          {group.title}
        </Link>
        {group.is_overdue ?
          <OverlayTrigger
            key={"overdue"}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-overdue`}>
                This veterinary task is overdue.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faCalendarExclamation} style={{ color: "#ff4c4c" }} className="ms-1" />
          </OverlayTrigger>
        : ""}
      </div>
    );
  };

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({id:'', exams:[], diagnostic_objects:[], procedure_objects:[], discharge_summaries:[], patient:null, vet_requests:[], pending:[], open: '', images: [], diagnosis: '', other_diagnosis:'', medical_plan:'', medical_notes:[], treatment_requests:[], animal_object: {id:'', name:'', species:'', category:'', sex:'', age:'', fixed:'', pcolor:'', scolor:'', medical_notes:'', shelter_object:{}, room_name:'', owners:[], owner_contacts:[]}});
  const [isLoading, setIsLoading] = useState(true);
  const [calendarData, setCalendarData] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [showCalendar, setShowCalendar] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchMedRecordData = async () => {
      // Fetch MedicalRecord details data.
      await axios.get('/vet/api/medrecord/' + id + '/?incident=' + state.incident.id, {
          cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let group_data = [];
          let calendar_data = [];
          let id_tracker = 0;
          response.data.treatment_plans.forEach(treatment => {
            let is_overdue = false;
            treatment.treatment_requests.forEach(treatment_request => {
              calendar_data.push({id:'treatment_request' + treatment_request.id, group:id_tracker, title:dayjs(treatment_request.suggested_admin_time).format('HH:mm'), start_time: dayjs(treatment_request.suggested_admin_time), end_time: dayjs(treatment_request.suggested_admin_time).add(1, 'hour'),
                itemProps: {
                  style: {
                    background: dayjs(treatment_request.suggested_admin_time).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                    color: 'white',       // Text color
                    borderColor: dayjs(treatment_request.suggested_admin_time).isBefore(dayjs()) ? '#f63535' : '#1a6fb3'  // Border color
                  }
                }
              });
              if (!treatment_request.not_administered && !treatment_request.actual_admin_time && dayjs(treatment_request.suggested_admin_time).isBefore(dayjs())) {
                is_overdue = true;
              }
            });
            group_data.push({id:id_tracker, object_id:treatment.id, title:treatment.description, vet_type:'treatment_plan', medical_record:treatment.medical_record, is_overdue:is_overdue});
            id_tracker = id_tracker + 1;
          });

          response.data.diagnostic_objects.forEach(diagnostic => {
            let is_overdue = false;
            if (!diagnostic.complete && dayjs(diagnostic.open).isBefore(dayjs())) {
              is_overdue = true;
            }
            calendar_data.push({id:'diagnostic' + diagnostic.id, group:id_tracker, title:dayjs(diagnostic.open).format('HH:mm'), start_time: dayjs(diagnostic.open), end_time: dayjs(diagnostic.open).add(1, 'hour'),
              itemProps: {
                style: {
                  background: dayjs(diagnostic.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                  color: 'white',       // Text color
                  borderColor: dayjs(diagnostic.open).isBefore(dayjs()) ? '#f63535' : '#1a6fb3'  // Border color
                }
              }
            });
            group_data.push({id:id_tracker, object_id:diagnostic.id, title:diagnostic.name === 'Other' ? diagnostic.other_name : diagnostic.name, vet_type:'diagnostic', medical_record:diagnostic.medical_record, is_overdue:is_overdue});
            id_tracker = id_tracker + 1;
          });

          response.data.procedure_objects.forEach(procedure => {
            let is_overdue = false;
            if (!procedure.complete && dayjs(procedure.open).isBefore(dayjs())) {
              is_overdue = true;
            }
            calendar_data.push({id:'procedure' + procedure.id, group:id_tracker, title:dayjs(procedure.open).format('HH:mm'), start_time: dayjs(procedure.open), end_time: dayjs(procedure.open).add(1, 'hour'),
              itemProps: {
                style: {
                  background: dayjs(procedure.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                  color: 'white',       // Text color
                  borderColor: dayjs(procedure.open).isBefore(dayjs()) ? '#f63535' : '#1a6fb3'  // Border color
                }
              }
            });
            group_data.push({id:id_tracker, object_id:procedure.id, title:procedure.name === 'Other' ? procedure.other_name : procedure.name, vet_type:'procedure', medical_record:procedure.medical_record, is_overdue:is_overdue});
            id_tracker = id_tracker + 1;
          });

          group_data = [...group_data].sort((a, b) => a.title.localeCompare(b.title));

          setCalendarData(calendar_data);
          setGroupData(group_data);
          setData(response.data);
        }
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
        return null;
      })
      .finally(() => setIsLoading(false));
    };
    fetchMedRecordData();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, incident]);

  return (
    <>
    {/* <Header>
      <span style={{cursor:'pointer'}} onClick={() => state.prevLocation ? navigate(state.prevLocation) : navigate("/" + organization + "/" + incident + "/vet/medrecord/" + data.medical_record)} className="me-2"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="sm" inverse /></span>
      Patient Calendar View
    </Header>
    <Patient
      animal={data.animal_object}
      organization={organization}
      incident={incident}
      medical_plan={data.medical_plan}
    /> */}
    <div className="row mt-3 mb-2">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
              <h4 className="h4 mb-0 pb-0 pt-2">
                Open Treatments/Diagnostics/Procedures
                <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showCalendar} onClick={() => {setShowCalendar(false)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <FontAwesomeIcon icon={faChevronCircleDown} hidden={showCalendar} onClick={() => {setShowCalendar(true)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
              </h4>
              {(state.user.is_superuser || state.user.vet_perms) ? <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"1px"}}>
                <LoadingLink
                  href={"/" + organization + "/" + incident + "/vet/medrecord/" + data.id + "/treatment/new"}
                  isLoading={isLoading}
                  className="d-block"label="Add Treatment"
                  disabled={data.exams.length === 0}
                  disabled_text="Patient must be examined first."
                >
                  <FontAwesomeIcon icon={faPrescriptionBottlePill} className="fas" inverse />
                </LoadingLink>
                <LoadingLink
                  href={"/" + organization + "/" + incident + "/vet/medrecord/" + data.id + "/diagnostics"}
                  isLoading={isLoading}
                  className="d-block"
                  label="Order Diagnostics"
                  disabled={data.exams.length === 0}
                  disabled_text="Patient must be examined first."
                >
                  <FontAwesomeIcon icon={faVial} className="fas" style={{cursor:'pointer'}} transform='grow-2' inverse />
                </LoadingLink>
                <LoadingLink
                  href={"/" + organization + "/" + incident + "/vet/medrecord/" + data.id + "/procedures"}
                  isLoading={isLoading}
                  className="d-block"
                  label="Order Procedures"
                  disabled={data.exams.length === 0}
                  disabled_text="Patient must be examined first."
                >
                  <FontAwesomeIcon icon={faScalpelLineDashed} className="fas" style={{cursor:'pointer'}} transform='grow-1' inverse />
                </LoadingLink>
              </ActionsDropdown> : ""}
            </div>
            <Collapse in={showCalendar}>
              <div>
                <p className="phr" />
                <Timeline
                  groups={groupData}
                  items={calendarData}
                  defaultTimeStart={dayjs(currentDate).startOf('day')}
                  defaultTimeEnd={dayjs(currentDate).endOf('day')}
                  visibleTimeStart={dayjs(currentDate).startOf('day')}
                  visibleTimeEnd={dayjs(currentDate).endOf('day')}
                  groupRenderer={renderGroup}
                  sidebarWidth={200}
                  onTimeChange={(_visibleStart, _visibleEnd, updateScrollCanvas) => {
                    // Lock the canvas back to the single day bounds
                    updateScrollCanvas(dayjs(currentDate).startOf('day'), dayjs(currentDate).endOf('day'));
                  }}
                >
                  <TimelineHeaders>
                    <DateHeader
                      unit="day"
                      intervalRenderer={({ getIntervalProps, intervalContext }) => {
                        const { key, ...intervalProps } = getIntervalProps();
                        return (
                        <div key={key}>
                          <div {...intervalProps} className="" style={{
                            ...intervalProps.style,
                            display: 'flex',
                            justifyContent: 'flex-start', // Align content to the far left
                            paddingLeft: '2px',          // Add spacing from the edge
                          }}>
                            <OverlayTrigger
                              key={"previous-day"}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-previous-day`}>
                                  Go to previous day.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faSquareChevronLeft} onClick={() => {setCurrentDate(dayjs(currentDate).add(-1, 'day'))}} size="2x" style={{ cursor: 'pointer', marginTop:"-26px" }} inverse />
                            </OverlayTrigger>
                          </div>
                          <div className="float-end" style={{
                            ...intervalProps.style,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'end',
                            cursor: 'pointer',          // Add spacing from the edge
                            paddingRight: '4px',
                          }}>
                            <OverlayTrigger
                              key={"next-day"}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-next-day`}>
                                  Go to next day.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faSquareChevronRight} onClick={() => {setCurrentDate(dayjs(currentDate).add(1, 'day'))}} size="2x" style={{ cursor: 'pointer', marginTop:"-26px" }} inverse />
                            </OverlayTrigger>
                          </div>
                          <span className="" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <span style={{marginTop:"4px"}}>{dayjs(currentDate).format('dddd, MMMM D, YYYY')}</span>
                          </span>
                        </div>
                        );
                      }}
                    />
                    <DateHeader
                      unit="hour"
                    />
                  </TimelineHeaders>
                </Timeline>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      </div>
    </div>
    </>
  );
};

export default PatientCalendarView;
